import {useEffect, useState} from 'react';
import {useQuery} from '@apollo/client';
import {Button, Spin} from 'antd';
import {useNavigate, useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {makeSelectTabOperations} from 'redux/store/auth';
import {NavTabsEnum} from 'utils/constants';
import {
  CHANGE_SUBSCRIPTION_PLAN_CATALOG_STATUS,
  GET_PLAN_SUBSCRIPTIONS_CATALOGS,
} from '../resolvers';
import SubscriptionCatalogItemModal from '../components/SubscriptionCatalogItemModal';
import SubscriptionResourceStatusModal from '../components/SubscriptionResourceStatusModal';
import styles from '../subscriptionSettings.module.scss';
import {
  formatDuration,
  getCouponLabel,
  getPrimaryLocaleText,
  getRenewalLabel,
  humanizeKey,
  PriceSummary,
  renderLocalizedOrDash,
} from './PlanSubscriptionsHelpers';

const PlanSubscriptionsPage = () => {
  const navigate = useNavigate();
  const {planKey} = useParams();

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.Subscription),
  );

  const [catalogModal, setCatalogModal] = useState({
    open: false,
    editContext: null,
  });
  const [statusModal, setStatusModal] = useState({
    open: false,
    itemData: null,
  });

  const {data, loading, refetch} = useQuery(GET_PLAN_SUBSCRIPTIONS_CATALOGS, {
    variables: {
      filter: {
        plan: planKey,
      },
    },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });

  const response = data?.getSubscriptionPlansCatalog;

  useEffect(() => {
    if (response?.__typename === 'Error') {
      toast.error(response.message);
    }
  }, [response]);

  const catalogItems =
    response?.__typename === 'ResponseWithSubscriptionCatalog'
      ? response?.data || []
      : [];

  const catalogPlanForCreate = catalogItems[0]?.plan ?? planKey;

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spin size="large" />
        <h1>Loading subscriptions...</h1>
      </div>
    );
  }

  return (
    <div className={styles['subscriptions-page']}>
      <div className={styles['subscriptions-toolbar']}>
        <div className={styles['subscriptions-header']}>
          <Button
            type="default"
            onClick={() =>
              navigate('/dashboard/plans/subscription-setting/plans')
            }>
            Back to Plans
          </Button>
          <div className={styles['subscriptions-title-block']}>
            <h1 className={styles['subscriptions-title']}>
              {humanizeKey(planKey)}
            </h1>
          </div>
        </div>
        <Button
          type="primary"
          disabled={!tabOperations?.create}
          onClick={() => setCatalogModal({open: true, editContext: null})}>
          Create Subscription Plan
        </Button>
      </div>

      {!catalogItems.length ? (
        <div className={styles['empty-state']}>
          No subscription plans found.
        </div>
      ) : (
        <div className={styles['items-grid']}>
          {catalogItems.map(item => {
            const appleProductId = item?.appleProductId?.trim?.();
            const redirectUrl = item?.redirectUrl?.trim?.();

            return (
              <article
                key={item?.key}
                className={styles['subscription-item-card']}>
                <div className={styles['item-card-head']}>
                  <div className={styles['item-title-row']}>
                    <h2 className={styles['item-title']}>
                      {getPrimaryLocaleText(item?.label)}
                    </h2>
                    <div className={styles['item-actions']}>
                      <Button
                        type="default"
                        size="small"
                        disabled={!tabOperations?.update || !item?.key}
                        onClick={() =>
                          setStatusModal({
                            open: true,
                            itemData: item,
                          })
                        }>
                        Change Status
                      </Button>
                      <Button
                        type="primary"
                        size="small"
                        disabled={!tabOperations?.update}
                        onClick={() =>
                          setCatalogModal({
                            open: true,
                            editContext: {
                              item,
                              catalogPlan: item?.plan,
                            },
                          })
                        }>
                        Edit
                      </Button>
                    </div>
                  </div>
                </div>

                <div className={styles['item-description-block']}>
                  {renderLocalizedOrDash(item?.description)}
                </div>

                <div className={styles['item-quick-facts']}>
                  <span className={styles['fact-tag']}>
                    {humanizeKey(item?.purchaseType)}
                  </span>
                  {item?.purchaseType === 'TIME_BASED' && (
                    <span className={styles['fact-tag']}>
                      Duration: {formatDuration(item?.duration)}
                    </span>
                  )}
                  {getRenewalLabel(item) ? (
                    <span className={styles['fact-tag']}>
                      {getRenewalLabel(item)}
                    </span>
                  ) : null}
                  <span className={styles['fact-tag']}>
                    {getCouponLabel(item?.allowCoupon)}
                  </span>
                  <span
                    className={
                      item?.isEnabled
                        ? styles['fact-tag-on']
                        : styles['fact-tag-off']
                    }>
                    {item?.isEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>

                <div className={styles['prices-section']}>
                  <PriceSummary prices={item?.prices} />
                </div>

                {(appleProductId || redirectUrl) && (
                  <div className={styles['item-tech']}>
                    {appleProductId ? (
                      <span>
                        App Store Product ID: <code>{appleProductId}</code>
                      </span>
                    ) : null}
                    {redirectUrl ? (
                      <span>
                        Redirect URL: <code>{redirectUrl}</code>
                      </span>
                    ) : null}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      <SubscriptionCatalogItemModal
        isOpen={catalogModal.open}
        onClose={() => setCatalogModal({open: false, editContext: null})}
        planKey={planKey}
        catalogPlan={
          catalogModal.editContext?.catalogPlan ?? catalogPlanForCreate
        }
        editContext={catalogModal.editContext}
        refetch={refetch}
        canSubmit={
          catalogModal.editContext?.item
            ? tabOperations?.update
            : tabOperations?.create
        }
      />

      <SubscriptionResourceStatusModal
        isOpen={statusModal.open}
        onClose={() => setStatusModal({open: false, itemData: null})}
        resourceData={
          statusModal.itemData
            ? {
                key: statusModal.itemData?.key,
                name:
                  getPrimaryLocaleText(statusModal.itemData?.label) ||
                  statusModal.itemData?.key,
                isEnabled: Boolean(statusModal.itemData?.isEnabled),
              }
            : null
        }
        refetch={refetch}
        canSubmit={tabOperations?.update}
        mutation={CHANGE_SUBSCRIPTION_PLAN_CATALOG_STATUS}
        mutationName="changeSubscriptionCatalogItemStatus"
        successTypename="ResponseWithSubscriptionCatalogItem"
        resourceLabel="Subscription"
      />
    </div>
  );
};

export default PlanSubscriptionsPage;
